import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import BASE_URL from '../config';

const notifyAuthFailure = () => {
  Cookies.remove('access_token');
  Cookies.remove('refresh_token');

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth:expired'));
  }
};

// Function to refresh the access token
export const refreshAuthToken = async (refreshToken) => {
  try {
    const response = await fetch(`${BASE_URL}/users/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    
    // Set the new access token in cookies
    Cookies.set('access_token', data.access, { sameSite: 'Strict', expires: 1 });

    // Optionally update refresh token if returned
    if (data.refresh) {
      Cookies.set('refresh_token', data.refresh, { sameSite: 'Strict', expires: 3 });
    }

    // Return the new access token
    return data.access;
  } catch (error) {
    throw new Error(`Token refresh error: ${error.message}`);
  }
};

// Helper function to decode the JWT and get the expiration time
const getTokenExpiration = (token) => {
  const decodedToken = jwtDecode(token);
  return decodedToken.exp * 1000; // `exp` is in seconds, convert to milliseconds
};

// Helper function to check if the token is expiring soon
const isExpiringSoon = (token, bufferMs = 60000) => {
  const expirationTime = getTokenExpiration(token);
  const currentTime = Date.now();
  const timeLeft = expirationTime - currentTime;
  
  return timeLeft <= 0 || timeLeft < bufferMs;
};

const fetchWithAccessToken = (url, options, accessToken) => fetch(url, {
  ...options,
  headers: {
    ...options.headers,
    Authorization: `Bearer ${accessToken}`,
  },
});

// Function to fetch data with automatic token refresh if needed
export const fetchWithAuth = async (url, options = {}) => {
  let accessToken = Cookies.get('access_token');
  const refreshToken = Cookies.get('refresh_token');

  if (!accessToken || !refreshToken) {
    throw new Error('No access or refresh token available. Please log in.');
  }

  // Check if the access token is close to expiring
  if (isExpiringSoon(accessToken)) {
    try {
      // Refresh the access token if it's close to expiring
      accessToken = await refreshAuthToken(refreshToken);
    } catch (error) {
      notifyAuthFailure();
      throw new Error('Failed to refresh access token. Please log in again.');
    }
  }

  let response = await fetchWithAccessToken(url, options, accessToken);

  if (response.status === 401) {
    let refreshedAccessToken;
    try {
      refreshedAccessToken = await refreshAuthToken(refreshToken);
    } catch (error) {
      notifyAuthFailure();
      throw new Error('Failed to refresh access token. Please log in again.');
    }

    try {
      response = await fetchWithAccessToken(url, options, refreshedAccessToken);
    } catch (error) {
      throw new Error('Network error after token refresh.');
    }

    if (response.status === 401) {
      notifyAuthFailure();
      throw new Error('Failed to refetch after token refresh. Please log in again.');
    }
  }

  return response;
};

export const getOrRefreshAccessToken = async (bufferMs = 60000) => {
  let accessToken = Cookies.get('access_token');
  const refreshToken = Cookies.get('refresh_token');

  if (!accessToken || !refreshToken) {
    notifyAuthFailure();
    throw new Error('No access or refresh token available. Please log in.');
  }

  if (isExpiringSoon(accessToken, bufferMs)) {
    try {
      accessToken = await refreshAuthToken(refreshToken);
    } catch (error) {
      notifyAuthFailure();
      throw new Error('Failed to refresh access token. Please log in again.');
    }
  }
  return accessToken;
};

export const xhrWithAuth = async (url, { method = "POST", body, onProgress, signal, uploadBufferMs = 600000 }) => {
  // Pre-emptively refresh access token with a safety buffer for heavy files
  const token = await getOrRefreshAccessToken(uploadBufferMs);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let activeXhr = xhr; // Track active XHR to support redirection during 401 retry

    const onAbort = () => {
      cleanup(); // Clean up listeners instantly upon cancellation to avoid leaks
      activeXhr.abort();
      reject(new DOMException("Request aborted", "AbortError"));
    };

    // Initialize Abort Controller listener
    if (signal) {
      if (signal.aborted) {
        activeXhr.abort();
        return reject(new DOMException("Request aborted", "AbortError"));
      }
      signal.addEventListener("abort", onAbort);
    }

    const cleanup = () => {
      if (signal) {
        signal.removeEventListener("abort", onAbort);
      }
    };

    xhr.open(method, url);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete, event.loaded, event.total);
        }
      };
    }

    xhr.onload = async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        cleanup();
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          resolve(xhr.responseText);
        }
      } else if (xhr.status === 401) {
        try {
          const refreshToken = Cookies.get('refresh_token');
          const newToken = await refreshAuthToken(refreshToken);
          
          // Guard against cancellation during token refresh process
          if (signal && signal.aborted) {
            return;
          }

          const retryXhr = new XMLHttpRequest();
          activeXhr = retryXhr; // Redirect abort listener to retry instance
          
          retryXhr.open(method, url);
          retryXhr.setRequestHeader("Authorization", `Bearer ${newToken}`);
          
          if (retryXhr.upload && onProgress) {
            retryXhr.upload.onprogress = xhr.upload.onprogress;
          }

          retryXhr.onload = () => {
            cleanup();
            if (retryXhr.status >= 200 && retryXhr.status < 300) {
              try { resolve(JSON.parse(retryXhr.responseText)); } catch { resolve(retryXhr.responseText); }
            } else {
              if (retryXhr.status === 401) notifyAuthFailure();
              reject({ status: retryXhr.status, statusText: retryXhr.statusText, responseText: retryXhr.responseText, url });
            }
          };

          retryXhr.onerror = () => {
            cleanup();
            reject({ status: 0, message: "Network error during retry", url });
          };

          retryXhr.send(body);
        } catch (refreshError) {
          cleanup();
          notifyAuthFailure();
          reject({ status: 401, message: "Failed to refresh token", error: refreshError });
        }
      } else {
        cleanup();
        reject({ status: xhr.status, statusText: xhr.statusText, responseText: xhr.responseText, url });
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject({ status: 0, message: "Network error", url });
    };

    xhr.ontimeout = () => {
      cleanup();
      reject({ status: 0, message: "Upload timeout", url });
    };

    xhr.send(body);
  });
};

